/**
 * Fetch and insert product badges into product cards on page load.
 */
document.addEventListener("DOMContentLoaded", function () {
  populateProductBadges();
});

/**
 * Update product badging when Bloomreach updates search results
 * This is documented here: https://documentation.bloomreach.com/discovery/docs/shopify-app-user-guide#226
 */
document.addEventListener("bloomreach:searchResults:updated", function () {
  populateProductBadges();
});

const CACHE_KEY = "dusk_product_badges_cache";
const CACHE_DURATION_MS = 10 * 60 * 1000; // 10 minutes

const populateProductBadges = () => {
  const productBadges = document.querySelectorAll("product-badge");
  if (productBadges.length > 0) {
    fetchProductBadges(productBadges).then((data) => {
      productBadges.forEach((badge) => {
        const productId = badge.dataset.productId;
        const badgeData = data.badges[productId]; // Use productId directly

        if (badgeData) {
          const badgeIcon = badgeData.icon
            ? `<svg xmlns="http://www.w3.org/2000/svg" class="icon icon--tag" width="16" height="16" viewBox="0 0 16 16" fill="none">
            <path d="M13.2538 7.88857L12.9046 3.57025C12.8728 3.31624 12.6823 3.12575 12.4283 3.09396L8.10996 2.74469C7.9512 2.74469 7.79239 2.8082 7.68127 2.91932L0.933897 9.66669C0.72748 9.87311 0.711617 10.2065 0.918034 10.4129L5.58563 15.0805C5.79204 15.2869 6.12543 15.2711 6.33184 15.0646L13.0792 8.31727C13.1903 8.20616 13.2538 8.04733 13.2538 7.88857ZM10.3644 5.63415C10.0627 5.33249 10.0627 4.82448 10.3802 4.50696C10.6978 4.18943 11.2058 4.18943 11.5074 4.49109C11.8091 4.79275 11.8091 5.30077 11.4916 5.61829C11.1899 5.91995 10.666 5.93581 10.3644 5.63415Z" fill="currentColor"/>
          </svg>`
            : "";

          badge.innerHTML = `
            <div class="card__badge card__badge--${badgeData.type?.toLowerCase()} card__badge--${badgeData.style?.toLowerCase()}">
              <span class="badge">
                ${badgeIcon}
                ${badgeData.label}
              </span>
            </div>
          `;
        } else {
          badge.innerHTML = "";
        }
      });
    });
  }
};

const fetchProductBadges = async (productBadgeElements) => {
  const productIds = Array.from(productBadgeElements).map((badge) => {
    return badge.dataset.productId;
  });

  const response = await fetch(
    `https://${window.Shopify.shop}/api/2025-01/graphql.json`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-Shopify-Storefront-Access-Token": "fa461acd789eda3169914d681fe74ba9",
      },
      body: JSON.stringify({
        query: `
        query {
          metaobjects(first: 25, type: "product_card_badge", sortKey: "id") {
            edges {
              node {
                id
                fields {
                  key
                  value
                }
              }
            }
          }
          products(first: 200, query: "${productIds.join(" OR id:")}") {
            edges {
              node {
                id
                handle
                tags
                availableForSale
                selectedOrFirstAvailableVariant {
                  compareAtPrice {
                    amount
                  }
                  price {
                    amount
                  }
                }
              }
            }
          }
        }
      `,
      }),
    }
  );

  // Load cache
  let cache = {};
  let cacheTimestamp = 0;
  try {
    const cacheRaw = sessionStorage.getItem(CACHE_KEY);
    if (cacheRaw) {
      const parsed = JSON.parse(cacheRaw);
      cache = parsed.data || {};
      cacheTimestamp = parsed.timestamp || 0;
    }
  } catch (e) {
    cache = {};
    cacheTimestamp = 0;
  }

  const now = Date.now();
  const cacheValid = cacheTimestamp && now - cacheTimestamp < CACHE_DURATION_MS;

  // Determine which productIds are missing from cache
  const missingProductIds = cacheValid
    ? productIds.filter((pid) => !cache[pid])
    : productIds;

  let badgesToReturn = {};
  if (cacheValid) {
    // Use cached badges for available productIds
    productIds.forEach((pid) => {
      if (cache[pid]) {
        badgesToReturn[pid] = cache[pid];
      }
    });
  }

  // If all badges are cached and valid, return them
  if (missingProductIds.length === 0 && cacheValid) {
    return { badges: badgesToReturn };
  }

  // Helper to batch productIds into chunks of 100
  const chunkArray = (arr, size) => {
    const result = [];
    for (let i = 0; i < arr.length; i += size) {
      result.push(arr.slice(i, i + size));
    }
    return result;
  };

  const fetchMetaobjects = async () => {
    const response = await fetch(
      `https://${window.Shopify.shop}/api/2025-01/graphql.json`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-Shopify-Storefront-Access-Token":
            "fa461acd789eda3169914d681fe74ba9",
        },
        body: JSON.stringify({
          query: `
          query {
            metaobjects(first: 25, type: "product_card_badge", sortKey: "id") {
              edges {
                node {
                  id
                  fields {
                    key
                    value
                  }
                }
              }
            }
          }
        `,
        }),
      }
    );
    const { data } = await response.json();
    if (!data || data.errors || !data.metaobjects) {
      console.error("Error fetching metaobjects", data?.errors);
      return [];
    }
    return data.metaobjects.edges;
  };

  const fetchProductsBatch = async (ids) => {
    const response = await fetch(
      `https://${window.Shopify.shop}/api/2025-01/graphql.json`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-Shopify-Storefront-Access-Token":
            "fa461acd789eda3169914d681fe74ba9",
        },
        body: JSON.stringify({
          query: `
          query {
            products(first: 250, query: "${ids.join(" OR id:")}") {
              edges {
                node {
                  id
                  handle
                  tags
                  availableForSale
                  selectedOrFirstAvailableVariant {
                    compareAtPrice {
                      amount
                    }
                    price {
                      amount
                    }
                  }
                }
              }
            }
          }
        `,
        }),
      }
    );
    const { data } = await response.json();
    if (!data || data.errors || !data.products) {
      console.error("Error fetching products", data?.errors);
      return [];
    }
    return data.products.edges;
  };

  // Fetch metaobjects and any missing products in parallel
  const metaobjectsPromise = fetchMetaobjects();
  const productIdBatches = chunkArray(missingProductIds, 250);
  const productBatchPromises = productIdBatches.map(fetchProductsBatch);

  const metaobjectsEdges = await metaobjectsPromise;
  const productsEdges = (await Promise.all(productBatchPromises)).flat();

  if (
    !metaobjectsEdges.length ||
    (!productsEdges.length && missingProductIds.length > 0)
  ) {
    if (Object.keys(badgesToReturn).length > 0) {
      // Return what we have in cache
      return { badges: badgesToReturn };
    }
    console.error("No metaobjects or products found in response");
    return;
  }

  const productBadges = metaobjectsEdges.map((badge) => {
    const badgeData = {};
    const fields = badge.node.fields;

    fields.forEach((field) => {
      let value = field.value;
      // Check if the value looks like a JSON array
      if (value.startsWith("[") && value.endsWith("]")) {
        try {
          value = JSON.parse(value);
        } catch (e) {
          console.warn(`Failed to parse JSON array for field ${field.key}:`, e);
        }
      }
      badgeData[field.key] = value;
    });

    return {
      id: badge.node.id,
      ...badgeData,
    };
  });

  const productData = productsEdges.map((product) => {
    return {
      id: product.node.id,
      handle: product.node.handle,
      tags: product.node.tags,
      availableForSale: product.node.availableForSale,
      selectedOrFirstAvailableVariant: {
        compareAtPrice:
          product.node.selectedOrFirstAvailableVariant.compareAtPrice?.amount ||
          undefined,
        price: product.node.selectedOrFirstAvailableVariant.price.amount,
      },
    };
  });

  // Only process badges for missing products
  const productBadgesToDisplay = productBadges.reduce((acc, badge) => {
    const currentDate = new Date();

    // Don't display badges if we're outside their start and/or end dates, if defined
    if (badge.start_date && new Date(badge.start_date) > currentDate)
      return acc;
    if (badge.end_date && new Date(badge.end_date) < currentDate) return acc;

    productData.forEach((product) => {
      if (
        !missingProductIds.includes(
          product.id.replace("gid://shopify/Product/", "")
        )
      )
        return acc;

      let badgeApplies = true;

      if (badge.criteria_stock_status) {
        const stockStatus = product.availableForSale
          ? "In stock"
          : "Out of stock";
        if (stockStatus !== badge.criteria_stock_status) {
          badgeApplies = false;
        }
      }

      if (
        badge.criteria_tags &&
        !product.tags.some(
          (tag) =>
            tag.toLowerCase() === String(badge.criteria_tags).toLowerCase()
        )
      ) {
        badgeApplies = false;
      }

      if (badgeApplies) {
        if (!acc[product.id]) {
          acc[product.id] = [];
        }

        if (badge.type === "Discount") {
          // Show discount badge with calculated savings if product is on sale
          if (
            product.selectedOrFirstAvailableVariant.compareAtPrice >
            product.selectedOrFirstAvailableVariant.price
          ) {
            const savings = (
              product.selectedOrFirstAvailableVariant.compareAtPrice -
              product.selectedOrFirstAvailableVariant.price
            ).toFixed(2);
            acc[product.id].push({
              type: badge.type,
              label: `Save £${savings}`,
              priority: badge.priority || 0,
              style: badge.style || "sale",
              icon: badge.icon || null,
            });
          }
        } else if (badge.label) {
          acc[product.id].push({
            type: badge.type,
            label: badge.label,
            priority: badge.priority || 0,
            style: badge.style || "sale",
            icon: badge.icon || null,
          });
        }
      }
      return acc;
    });
    return acc;
  }, {});

  // Select the highest priority badge from all the badges for each product
  Object.keys(productBadgesToDisplay).forEach((productId) => {
    productBadgesToDisplay[productId] = productBadgesToDisplay[
      productId
    ].reduce(
      (highest, current) =>
        !highest || current.priority > highest.priority ? current : highest,
      null
    );
  });

  // Merge new badges into cache
  Object.entries(productBadgesToDisplay).forEach(([pid, badge]) => {
    // pid is Shopify GID, but cache keys are productId (number)
    const shortId = pid.replace("gid://shopify/Product/", "");
    badgesToReturn[shortId] = badge;
    cache[shortId] = badge;
  });

  // Save updated cache
  try {
    sessionStorage.setItem(
      CACHE_KEY,
      JSON.stringify({ data: cache, timestamp: now })
    );
  } catch (e) {
    // Ignore cache write errors
  }

  const returnData = {
    badges: badgesToReturn,
  };

  return returnData;
};
